import { z } from 'zod';
import { TimestampSchema } from '../common.js';
import { AvatarArtifactRuntimeBindingSchema, Sha256HexSchema } from './avatarProfile.js';
export const AVATAR_ANIMATION_CONTRACT_VERSION = 4;
/** Product acceptance target; the asynchronous pilot does not satisfy this target. */
export const AVATAR_ANIMATION_APPLICATION_TARGET_MS = 90_000;
/** Immutable deadline retained when reading historical contract-v3 jobs. */
export const AVATAR_ANIMATION_V3_RENDER_DEADLINE_MS = 90_000;
/** Bounded execution envelope for the controlled asynchronous contract-v4 pilot. */
export const AVATAR_ANIMATION_PILOT_RENDER_DEADLINE_MS = 7 * 60_000;
export const AVATAR_ANIMATION_RENDER_PROFILE = 'latentsync-eager-v3-async-pilot-v1';
export const AVATAR_ANIMATION_MAX_SPEECH_CODE_POINTS = 240;
export const AVATAR_ANIMATION_MAX_SPEECH_UTF8_BYTES = 1_024;
export const AVATAR_ANIMATION_SAMPLE_RATE_HZ = 16_000;
export const AVATAR_ANIMATION_MAX_AUDIO_SAMPLES = 80_000;
export const AVATAR_ANIMATION_MAX_PCM_BYTES = 160_000;
export const AVATAR_ANIMATION_MAX_VIDEO_FRAMES = 125;
export const AVATAR_ANIMATION_MAX_OUTPUT_BYTES = 128 * 1_024 * 1_024;
function hasUnpairedSurrogate(value) {
    for (let index = 0; index < value.length; index += 1) {
        const codeUnit = value.charCodeAt(index);
        if (codeUnit >= 0xd800 && codeUnit <= 0xdbff) {
            if (index + 1 >= value.length)
                return true;
            const next = value.charCodeAt(index + 1);
            if (next < 0xdc00 || next > 0xdfff)
                return true;
            index += 1;
        }
        else if (codeUnit >= 0xdc00 && codeUnit <= 0xdfff) {
            return true;
        }
    }
    return false;
}
function utf8ByteLength(value) {
    let bytes = 0;
    for (const character of value) {
        const codePoint = character.codePointAt(0);
        bytes += codePoint <= 0x7f ? 1 : codePoint <= 0x7ff ? 2 : codePoint <= 0xffff ? 3 : 4;
    }
    return bytes;
}
export const AvatarAnimationSpeechTextSchema = z.string().superRefine((text, ctx) => {
    if (text.length === 0 || !/\S/u.test(text)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Avatar animation speech must contain a non-whitespace character.',
        });
    }
    if (hasUnpairedSurrogate(text)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Avatar animation speech must be well-formed Unicode text.',
        });
        return;
    }
    if (Array.from(text).length > AVATAR_ANIMATION_MAX_SPEECH_CODE_POINTS) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Avatar animation speech must not exceed ${AVATAR_ANIMATION_MAX_SPEECH_CODE_POINTS} Unicode code points.`,
        });
    }
    if (utf8ByteLength(text) > AVATAR_ANIMATION_MAX_SPEECH_UTF8_BYTES) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Avatar animation speech must not exceed ${AVATAR_ANIMATION_MAX_SPEECH_UTF8_BYTES} UTF-8 bytes.`,
        });
    }
});
export const AvatarAnimationSpeechV3Schema = z
    .object({
    provider: z.literal('aws-polly'),
    profileVersion: z.literal('polly-neural-pcm16-v1'),
    engine: z.literal('neural'),
    languageCode: z.literal('en-US'),
    voiceId: z.literal('Joanna'),
    outputFormat: z.literal('pcm'),
    sampleRateHz: z.literal(AVATAR_ANIMATION_SAMPLE_RATE_HZ),
    channels: z.literal(1),
    sampleFormat: z.literal('pcm_s16le'),
    textSource: z.literal('responseSnapshot.reply'),
    textType: z.literal('text'),
    textSha256: Sha256HexSchema,
    textCodePoints: z.number().int().min(1).max(AVATAR_ANIMATION_MAX_SPEECH_CODE_POINTS),
    textUtf8Bytes: z.number().int().min(1).max(AVATAR_ANIMATION_MAX_SPEECH_UTF8_BYTES),
    maxAudioSamples: z.literal(AVATAR_ANIMATION_MAX_AUDIO_SAMPLES),
    maxPcmBytes: z.literal(AVATAR_ANIMATION_MAX_PCM_BYTES),
})
    .strict();
export const AvatarAnimationOutputV3Schema = z
    .object({
    bucket: z.string().min(1).max(255),
    key: z.string().min(1).max(1_024),
    versionId: z
        .string()
        .min(1)
        .max(1_024)
        .refine((value) => value !== 'null'),
    etag: z.string().min(1).max(1_024),
    bytes: z.number().int().positive().max(AVATAR_ANIMATION_MAX_OUTPUT_BYTES),
    sha256: Sha256HexSchema,
    contentType: z.literal('video/mp4'),
    audioSampleCount: z.number().int().min(1).max(AVATAR_ANIMATION_MAX_AUDIO_SAMPLES),
    videoFrameCount: z.number().int().min(1).max(AVATAR_ANIMATION_MAX_VIDEO_FRAMES),
})
    .strict()
    .superRefine((output, ctx) => {
    const expectedFrames = Math.ceil((output.audioSampleCount * 25) / AVATAR_ANIMATION_SAMPLE_RATE_HZ);
    if (output.videoFrameCount !== expectedFrames) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['videoFrameCount'],
            message: 'Video frame count must exactly cover the bounded PCM sample count at 25 FPS.',
        });
    }
});
const WorkerAnimationIdentitySchema = z.object({
    tenantId: z.string().uuid(),
    sessionId: z.string().uuid(),
    animationId: z.string().uuid(),
});
const WorkerAnimationAttemptSchema = WorkerAnimationIdentitySchema.extend({
    renderAttemptId: z.string().uuid(),
});
export const AvatarAnimationWorkerFailureCodeSchema = z.enum([
    'artifact-validation-failed',
    'output-validation-failed',
    'render-deadline-exceeded',
    'renderer-failed',
    'speech-duration-exceeded',
    'speech-synthesis-failed',
    'worker-interrupted',
    'worker-resource-failed',
]);
export const AvatarAnimationWorkerInvocationSchema = z.discriminatedUnion('operation', [
    WorkerAnimationAttemptSchema.extend({
        schemaVersion: z.literal(1),
        operation: z.literal('claim'),
    }).strict(),
    WorkerAnimationAttemptSchema.extend({
        schemaVersion: z.literal(1),
        operation: z.literal('complete'),
        output: AvatarAnimationOutputV3Schema,
    }).strict(),
    WorkerAnimationAttemptSchema.extend({
        schemaVersion: z.literal(1),
        operation: z.literal('fail'),
        errorCode: AvatarAnimationWorkerFailureCodeSchema,
    }).strict(),
]);
export const AvatarAnimationClaimedWorkSchema = WorkerAnimationAttemptSchema.extend({
    contractVersion: z.literal(AVATAR_ANIMATION_CONTRACT_VERSION),
    renderProfile: z.literal(AVATAR_ANIMATION_RENDER_PROFILE),
    renderDeadlineAt: TimestampSchema,
    preparedArtifact: AvatarArtifactRuntimeBindingSchema,
    speech: AvatarAnimationSpeechV3Schema,
    speechText: AvatarAnimationSpeechTextSchema,
})
    .strict()
    .superRefine((work, ctx) => {
    const codePoints = Array.from(work.speechText).length;
    const utf8Bytes = utf8ByteLength(work.speechText);
    if (work.speech.textCodePoints !== codePoints) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['speech', 'textCodePoints'],
            message: 'Claimed speech code-point metadata must match the exact speech text.',
        });
    }
    if (work.speech.textUtf8Bytes !== utf8Bytes) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['speech', 'textUtf8Bytes'],
            message: 'Claimed speech byte metadata must match the exact speech text.',
        });
    }
    // SHA-256 is recomputed synchronously by the trusted lifecycle Lambda and worker.
});
export const AvatarAnimationWorkerResponseSchema = z.discriminatedUnion('result', [
    z
        .object({
        schemaVersion: z.literal(1),
        operation: z.literal('claim'),
        result: z.literal('claimed'),
        work: AvatarAnimationClaimedWorkSchema,
    })
        .strict(),
    z
        .object({
        schemaVersion: z.literal(1),
        operation: z.literal('claim'),
        result: z.literal('not-claimable'),
    })
        .strict(),
    z
        .object({
        schemaVersion: z.literal(1),
        operation: z.enum(['complete', 'fail']),
        result: z.enum(['accepted', 'not-accepted']),
    })
        .strict(),
]);
//# sourceMappingURL=avatarAnimation.js.map